import { useTranslation } from 'react-i18next';

import type { RunProgress } from '../types/run.api.types';
import { isGeneratedRejectNote, withStageLabels } from '../utils/batches';
import { failureCodeLabel } from '../utils/catalog-labels';
import { plainFailureReason } from '../utils/run-stage-reasons';

import { useRunLabels } from './use-run-labels';
import { useRunStageAttempts } from './use-run-stage-attempts';

/**
 * A stopped run's words, shared by every place that explains a stop: the title (the stage it
 * stopped at, or the failure code's plain title), the plain-language "why", the code's one-line
 * "next step", the raw reason and whether a person rejected it (vs a crash). Reads the stage's
 * attempt timeline (same cache as the expanded row) to tell a rejection apart.
 */
export function useRunFailureText(runId: string, progress: RunProgress) {
  const { t } = useTranslation('catalog-enrichment-run');
  const { t: tCatalog } = useTranslation('catalog-enrichment');
  const { stageLabel } = useRunLabels();
  const stage = progress.currentStage ?? null;
  const { data, isPending } = useRunStageAttempts(runId, stage ?? '', !!stage);
  const newest = data?.attempts.slice(-1)[0];
  const raw = (progress.failureReason ?? '').trim();
  // The words a person reads: raw stage codes ("…at stage ENRICHMENT") become step names.
  const readable = withStageLabels(raw, tCatalog);
  const code = failureCodeLabel(tCatalog, progress.failureCode);
  // A REJECTED run is an operator's deliberate call, not a crash; the failure code is the only
  // signal before the attempt timeline (which carries `gateOutcome`) has loaded.
  const rejected =
    progress.failureCode === 'OPERATOR_REJECTED' || newest?.gateOutcome === 'GATE_REJECT';

  const why = (() => {
    if (rejected) {
      return raw && !isGeneratedRejectNote(raw)
        ? t('failureSummary.rejected', { reason: readable })
        : t('failureSummary.rejectedNoReason');
    }
    if (!raw) return t('failureSummary.noReason');
    const reason = plainFailureReason(raw);
    if (reason.kind === 'gateway') return t('failureSummary.gateway', { code: reason.code });
    if (reason.kind === 'timeout') return t('failureSummary.timeout');
    return withStageLabels(reason.text, tCatalog);
  })();

  const title = stage
    ? t(rejected ? 'failureSummary.rejectedAt' : 'failureSummary.stoppedAt', {
        stage: stageLabel(stage),
      })
    : (code?.title ?? t('overview.failedTitle'));
  const next = code && code.next.length > 0 ? code.next : null;

  // Whether a stop was a rejection is known only once the attempts answer (absent a failure code).
  const ready = !stage || !isPending || progress.failureCode === 'OPERATOR_REJECTED';
  return { stage, title, why, next, raw, rejected, ready };
}
